import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  useGetApiRuntimeCronJobsCronJobIdRuns,
} from "@/lib/api/generated/runtime-cron-jobs/runtime-cron-jobs";
import type { RuntimeCronJobRecordDto } from "@/lib/api/generated/control-api.schemas";
import { formatTimestamp } from "@/lib/utils/formatting";
import { mapRunStatus, requireCronJobId, type RuntimeCronScope } from "./runtime-cron-utils";

export function RuntimeCronJobRunsDialog({
  tenantId,
  scope,
  job,
  open,
  onOpenChange,
}: {
  tenantId: string;
  scope: RuntimeCronScope;
  job: RuntimeCronJobRecordDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const cronJobId = job ? requireCronJobId(job) : "";
  const runsQueryParams = { tenantId, limit: 25, scope };
  const runsQuery = useGetApiRuntimeCronJobsCronJobIdRuns(
    cronJobId,
    runsQueryParams,
    {
      query: {
        enabled: open && cronJobId.length > 0,
      },
    },
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Runtime Cron Runs</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {job ? (
            <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              {job.name} ({cronJobId})
            </p>
          ) : null}
          {runsQuery.isLoading ? (
            <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
              Loading runs...
            </p>
          ) : (runsQuery.data ?? []).length === 0 ? (
            <div
              className="rounded-[var(--radius-sm)] border px-3 py-4 text-sm"
              style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
            >
              No runs yet.
            </div>
          ) : (
            <div className="max-h-[60vh] space-y-2 overflow-auto pr-1">
              {(runsQuery.data ?? []).map((run) => (
                <div
                  key={run.runId}
                  className="rounded-[var(--radius-sm)] border p-3"
                  style={{ borderColor: "var(--border)" }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <StatusBadge status={mapRunStatus(run.status ?? "")} />
                      <span className="text-xs font-mono" style={{ color: "var(--text-strong)" }}>
                        {run.runId}
                      </span>
                    </div>
                    <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {formatTimestamp(run.startedAt ?? undefined)}
                    </span>
                  </div>
                  <div className="mt-2 grid gap-2 text-xs md:grid-cols-2">
                    <Field label="Completed" value={formatTimestamp(run.completedAt ?? undefined)} />
                    <Field label="Duration" value={run.durationMs ? `${run.durationMs} ms` : "—"} />
                    <Field label="Provider" value={run.provider ?? "—"} mono />
                    <Field label="Model" value={run.model ?? "—"} mono />
                  </div>
                  {run.summary ? (
                    <p className="mt-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
                      {run.summary}
                    </p>
                  ) : null}
                  {run.error ? (
                    <div
                      className="mt-2 rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
                      style={{ borderColor: "var(--danger-border)", background: "var(--danger-soft)" }}
                    >
                      {run.error}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="space-y-1">
      <div style={{ color: "var(--muted-foreground)" }}>{label}</div>
      <div className={mono ? "font-mono text-[11px]" : undefined} style={{ color: "var(--text-strong)" }}>
        {value}
      </div>
    </div>
  );
}
